
import {
  TimesheetAdditionalConfigurationRaw,
  UserHolidayModel,
} from '@/lib/api/company/configuration/timesheet-additional/types'
import { FaIcon, faTrash } from '@/lib/fa'

import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { formatDate } from '@/modules/shared/utils/date'
import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  state: TimesheetAdditionalConfigurationRaw
  setState: React.Dispatch<React.SetStateAction<TimesheetAdditionalConfigurationRaw>>
}

type UserHolidayPatch = Partial<Pick<UserHolidayModel, 'name' | 'date' | 'isNew'>>

export const HolidaysTable = ({ state, setState }: Props) => {
  const { t } = useLocalize()
  const rows = state.userHolidays

  const todayIso = React.useMemo(() => formatDate(new Date(), 'YYYY-MM-DD'), [])

  function setRows(updater: (prev: UserHolidayModel[]) => UserHolidayModel[]) {
    setState((prevState) => ({
      ...prevState,
      userHolidays: updater(prevState.userHolidays),
    }))
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      { id: 0, name: '', date: todayIso, isNew: true },
    ])
  }

  function updateRow(id: number, patch: UserHolidayPatch) {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    )
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((r) => r.id !== id))
  }

  const wrapConfirm = useWrapConfirm()

  return (
    <div className="tw-w-full tw-max-w-4xl tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-px-5 tw-py-4">
        <div>
          <h2 className="tw-text-base tw-font-semibold tw-text-slate-900">
            {t('configuration-timesheet.additional-holidays')}
          </h2>
          <p className="tw-mt-1 tw-text-sm tw-text-slate-500">
            {t('configuration-timesheet.fill-days-off')}
          </p>
        </div>

        <button
          type="button"
          onClick={addRow}
          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
          aria-label={t('configuration-timesheet.add-holiday')}
          title={t('common.add')}
        >
          +
        </button>
      </div>

      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead>
            <tr className="tw-bg-slate-50">
              <th className="tw-px-5 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
                {t('common.name')}
              </th>
              <th className="tw-px-5 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
                {t('common.date')}
              </th>
              <th className="tw-w-16 tw-px-5 tw-py-3 tw-text-right tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600" />
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="tw-px-5 tw-py-8 tw-text-center tw-text-sm tw-text-slate-500"
                >
                  {t('configuration-timesheet.no-additional-days')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="tw-border-t tw-border-slate-200 hover:tw-bg-slate-50/60"
                >
                  <td className="tw-px-5 tw-py-3">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => updateRow(row.id, { name: e.target.value })}
                      placeholder={t('configuration-timesheet.holiday-name-placeholder')}
                      className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
                    />
                  </td>

                  <td className="tw-px-5 tw-py-3">
                    <input
                      type="date"
                      value={formatDate(row.date)}
                      onChange={(e) => updateRow(row.id, { date: e.target.value })}
                      className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
                    />
                  </td>

                  <td className="tw-px-5 tw-py-3 tw-text-right">
                    <button
                      type="button"
                      onClick={wrapConfirm(() => removeRow(row.id), {
                        message: t('configuration-timesheet.delete-day-confirm'),
                      })}
                      className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-p-2 tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
                      aria-label={t('common.delete')}
                      title={t('common.delete')}
                    >
                      <FaIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
